import classNames from 'classnames';
import moment from 'moment';
import { bool, func } from 'prop-types';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { compose } from 'redux';

import { isScrollingDisabled } from '../../ducks/ui.duck';
import { injectIntl, intlShape } from '../../util/reactIntl';
import { updateProfile } from '../ProfileSettingsPage/ProfileSettingsPage.duck';
import { verifyPhoneNumber } from './MerchantDetailsPage.duck';

import { useConfiguration } from '../../context/configurationContext';
import { convertToTitleCase, ensureCurrentUser } from '../../util/data';

import { NamedLink } from '../../components';
import IconCollection from '../../components/IconCollection/IconCollection';
import SignupFlowForm from '../../components/MerchantDetails/SignupFlowForm';
import VerificationCode from '../../components/MerchantDetails/VerificationForm';
import NotFoundPage from '../NotFoundPage/NotFoundPage';

import { useHistory } from 'react-router-dom/cjs/react-router-dom';
import merchatLogo from '../../assets/merchatLogo.png';
import CongratulationsForm from '../../components/MerchantDetails/CongratulationsForm';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { Events, mixPanelEventsConstants, mixPanelEventsData, mixPanelEventsScreens } from '../../mixpanel';
import { trackEventsMixpanel } from '../../mixpanel/mixpanel';
import { createResourceLocatorString } from '../../util/routes';
import { getOnboardingNavigationPath } from '../../util/onboardingHelpers';
import { getPostAuthIntent, isValidReturnPath, clearPostAuthIntent } from '../../util/postAuthIntent';
import { onCheckPhoneNumberExists } from '../../util/api';
import css from './MerchantDetailsPage.module.css';
import { fetchCurrentUser } from '../../ducks/user.duck';

export const UPDATE_PROFILE = "update-profile";
export const VERIFY_PHONE = "verify-phone";
export const CHOOSE_PROFILE = "choose-profile";

const draftId = '00000000-0000-0000-0000-000000000000';

// Format/parse date of birth
const formatDateOfBirth = (date) => date ? moment(date).format('DD/MM/YYYY') : "";
const restoreDateOfBirth = (date) => date ? moment(date, 'DD/MM/YYYY')?.format('YYYY-MM-DD') : "";

const MerchantDetailsPageComponent = (props) => {
  if (typeof window === 'undefined') return null;

  const {
    currentUser,
    onVerifyPhoneNumber,
    scrollingDisabled,
    onManageDisableScrolling,
    intl,
    params,
    staticContext,
    onUpdateProfile,
    updateProfileInProgress,
    updateProfileError,
    verifyOtpInProgress,
    verifyOtpSuccess,
    verifyOtpError,
  } = props;

  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();
  const history = useHistory();

  const screen = params?.screen;
  const validScreen = [UPDATE_PROFILE, VERIFY_PHONE, CHOOSE_PROFILE].includes(screen);
  if (!validScreen) return <NotFoundPage staticContext={staticContext} />;

  const ensuredCurrentUser = ensureCurrentUser(currentUser);
  const currentUserId = ensuredCurrentUser?.id?.uuid;
  const { email, profile } = ensuredCurrentUser?.attributes || {};
  const { firstName, lastName, publicData, protectedData } = profile || {};
  const { profileType, business_name, userType, isPhoneNumberVerified } = publicData || {};
  const { mobile_phone_number, phoneNumber } = protectedData || {};

  const restoredDateOfBirth = restoreDateOfBirth(protectedData?.dateOfBirth);


  const signupFlowFormInitialValues = {
    email,
    firstName,
    lastName,
    business_name,
    mobile_phone_number,
    phoneNumber,
    prot_dateOfBirth: restoredDateOfBirth,
    prot_gender: protectedData?.gender,
  };

  const congratulationsFormInitialValues = {
    usingNowFor: profileType,
  };

  const handleUpdateProfile = (values) => {
    const { firstName, lastName, displayName, prot_dateOfBirth, prot_gender, business_name } = values;
    const currentPhoneNumber = (phoneNumber || '').trim();
    const nextPhoneNumber = (values.phoneNumber || '').trim();
    const hasPhoneNumberChanged = currentPhoneNumber !== nextPhoneNumber;

    const displayNameMaybe = displayName
      ? { displayName: displayName.trim() }
      : { displayName: null };

    const updatedValues = {
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      ...displayNameMaybe,
      publicData: {
        business_name,
        ...(hasPhoneNumberChanged ? { isPhoneNumberVerified: false } : {}),
      },
      protectedData: {
        mobile_phone_number: values.mobile_phone_number,
        phoneNumber: values.phoneNumber,
        dateOfBirth: formatDateOfBirth(prot_dateOfBirth),
        gender: prot_gender,
      },
    };

    const updateProfileAction = hasPhoneNumberChanged
      ? onCheckPhoneNumberExists({ phoneNumber: nextPhoneNumber }).then(res => {
          if (res?.phoneNumberExists) {
            if (typeof window !== 'undefined') {
              window.scrollTo(0, 0);
              window.alert(intl.formatMessage({ id: 'SignupForm.phoneNumberAlreadyExists' }));
            }
            throw new Error('phone-taken');
          }

          return onUpdateProfile(updatedValues);
        })
      : onUpdateProfile(updatedValues);

    updateProfileAction
      .then(() => {
        console.log("✅ Profile updated successfully");
        history.push(createResourceLocatorString('MerchantDetailsPage', routeConfiguration, { screen: VERIFY_PHONE }));
      })
      .catch(err => {
        if (err?.message === 'phone-taken') {
          return;
        }
        console.error("❌ Update failed:", err?.message || err);
      });
  };

  const handleVerifyPhoneNumber = (values) => {
    const { verificationCode } = values;
    if (isPhoneNumberVerified) {
      history.push(
        createResourceLocatorString('MerchantDetailsPage', routeConfiguration, { screen: CHOOSE_PROFILE })
      );
      return;
    }

    onVerifyPhoneNumber({ phoneNumber, verificationCode })
      .then(valid => {
        if (valid) {
          console.log("✅ Phone number verified successfully");
          history.push(createResourceLocatorString('MerchantDetailsPage', routeConfiguration, { screen: CHOOSE_PROFILE }));
        }
      })
      .catch(err => {
        console.error("❌ Phone number verification failed:", err?.message || err);
      });
  };

  const handleChooseProfile = (values) => {
    const { usingNowFor } = values || {};
    if (usingNowFor) {
      const updatedValues = {
        publicData: { profileType: usingNowFor }
      };

      const objectiveMap = {
        reduceIdleCapacity: 'increase_revenue',
        sellInventory: 'better_marketing',
        manageSchedules: 'efficient_management',
      };

      const business_objective = objectiveMap[usingNowFor] || 'efficient_management';

      onUpdateProfile(updatedValues)
        .then(() => {
          trackEventsMixpanel(Events.MERCHANT_SIGNUP_COMPLETE,
            {
              action_type: mixPanelEventsData.BUSINESS_GOAL,
              screen: mixPanelEventsScreens.PROFILEBASICINFO,
              user_type: 'provider',
              user_id: currentUserId,
            }
          );
          const intent = getPostAuthIntent();
          if (intent?.returnPath && isValidReturnPath(intent.returnPath)) {
            clearPostAuthIntent();
            history.push(intent.returnPath);
          } else {
            // A brand-new merchant has no service/product listing yet, so this
            // routes through the category picker first — see
            // getOnboardingNavigationPath. If the agreement isn't signed yet,
            // that helper routes to the agreement step instead.
            history.push(
              getOnboardingNavigationPath(
                { id: draftId, formType: 'service' },
                routeConfiguration,
                currentUser
              )
            );
          }
        })
        .catch(err => {
          console.error("❌ Update choose profile failed:", err?.message || err);
        });
    }
  };

  const getComponentForScreen = (screen) => {
    switch (screen) {
      case UPDATE_PROFILE:
        return (
          <SignupFlowForm
            onSubmit={handleUpdateProfile}
            intl={intl}
            currentUser={currentUser}
            config={config}
            initialValues={signupFlowFormInitialValues}
            updateInProgress={updateProfileInProgress}
            updateError={updateProfileError}
            hideFieldLabels={true}
          />
        );
      case VERIFY_PHONE:
        return (
          <VerificationCode
            onSubmit={handleVerifyPhoneNumber}
            intl={intl}
            currentUser={currentUser}
            updateInProgress={verifyOtpInProgress}
            updateError={verifyOtpError}
          />
        );
      case CHOOSE_PROFILE:
        return (
          <CongratulationsForm
            onSubmit={handleChooseProfile}
            intl={intl}
            currentUser={currentUser}
            updateInProgress={updateProfileInProgress}
            updateError={updateProfileError}
            initialValues={congratulationsFormInitialValues}
          />
        );
      default:
        return null;
    }
  };

  const currentScreen = getComponentForScreen(screen);
  const arabicLanguage = localStorage.getItem("language") === "Arabic";

  const tabs = [
    {
      label: intl.formatMessage({ id: "MerchantDetails.yourDetails" }),
      content: intl.formatMessage({ id: "MerchantDetails.setupEmailAndPassword" }),
      key: UPDATE_PROFILE,
      activeCondition: true,
      isSelected: screen === UPDATE_PROFILE
    },
    {
      label: intl.formatMessage({ id: "MerchantDetails.verifyPhoneNumber" }),
      content: intl.formatMessage({ id: "MerchantDetails.enterOtp" }),
      key: VERIFY_PHONE,
      activeCondition: isPhoneNumberVerified,
      isSelected: screen === VERIFY_PHONE
    },
    {
      label: intl.formatMessage({ id: "MerchantDetails.welcomeToNow" }),
      content: intl.formatMessage({ id: "MerchantDetails.watchGuidedTutorial" }),
      key: CHOOSE_PROFILE,
      activeCondition: profileType || isPhoneNumberVerified,
      isSelected: screen === CHOOSE_PROFILE
    },
  ];

  return (
    <div className={classNames(css.container, arabicLanguage && css.arabicDirection)}>
      <div className={css.sidebar}>
        <div className={css.sideBarContent}>
          <div className={css.logo}>
            <NamedLink name="LandingPage">
              <img src={merchatLogo} alt="Logo" />
            </NamedLink>
          </div>
          <ul className={css.steps}>
            {tabs.map((tab, index) => (
              <li
                key={index}
                className={classNames(
                  css.step,
                  tab.activeCondition ? css.active : '',
                  tab.isSelected ? css.selected : ''
                )}
                onClick={() => {
                  if (tab.activeCondition) {
                    history.push(createResourceLocatorString('MerchantDetailsPage', routeConfiguration, { screen: tab.key }));
                  }
                }
                }
              >
                <span className={css.stepIcon}>
                  {index === 0 && <IconCollection name="tabDetails" />}
                  {index === 1 && <IconCollection name="tabCall" />}
                  {index === 2 && <IconCollection name="tabWelcome" />}
                </span>
                <div>
                  <p className={css.tabLabel}>{tab.label}</p>
                  <p className={css.tabDetails}>{tab.content}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className={css.mainContent}>
        {currentScreen}
      </div>
    </div>
  );
};

MerchantDetailsPageComponent.propTypes = {
  scrollingDisabled: bool.isRequired,
  onManageDisableScrolling: func.isRequired,
  intl: intlShape.isRequired,
};

const mapStateToProps = (state) => {
  const { currentUser } = state.user;
  const {
    isSubmitting,
    submissionSuccess,
    submissionError,
  } = state.MerchantDetailsPage || {};

  const { updateInProgress, updateProfileError } = state.ProfileSettingsPage || {};

  return {
    currentUser,
    verifyOtpInProgress: isSubmitting,
    verifyOtpSuccess: submissionSuccess,
    verifyOtpError: submissionError,
    scrollingDisabled: isScrollingDisabled(state),
    updateProfileInProgress: updateInProgress,
    updateProfileError: updateProfileError
  };
};

const mapDispatchToProps = (dispatch) => ({
  onVerifyPhoneNumber: (data) => dispatch(verifyPhoneNumber(data)),
  onUpdateProfile: (data) => dispatch(updateProfile(data)),
});

const MerchantDetailsPage = compose(
  withRouter,
  connect(mapStateToProps, mapDispatchToProps),
  injectIntl
)(MerchantDetailsPageComponent);

export default MerchantDetailsPage;
